import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import VitalCard from "./VitalCard";
import { Activity, Calendar } from "lucide-react";
import api from "../../../api";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import Txt from "../../../components/Text";
import formtDate from "../../../utils/formatDate";
import CustomSelect from "../../../components/CustomSelect";

const Text = ({ children, className, page = "VISITS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};



// Placeholder for VitalsSection
const VitalsSectionPlaceholder = ({ darkMode = false }) => {
  const isRTL = useSelector((state) => state.settings.language) === "ar";

  return (
    <div
      className={`rounded-2xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border p-5 md:p-6
                    ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-slate-100"}`}
    >
      {/* Header Placeholder */}
      <div className={`flex items-center justify-between mb-5 ${arClass(isRTL, "flex-row-reverse")}`}>
        <div className={`flex items-center gap-2.5 ${arClass(isRTL, "flex-row-reverse")}`}>
          <div
            className={`w-5 h-5 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`}
          />
          <div
            className={`h-6 w-40 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`}
          />
        </div>
        <div className={`flex gap-2 ${arClass(isRTL, "flex-row-reverse")}`}>
          <div
            className={`h-8 w-32 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`}
          />
          <div
            className={`h-8 w-24 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`}
          />
        </div>
      </div>

      {/* Grid of 4 vitals placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className={`rounded-xl border p-4 md:p-5
                           ${darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-slate-100"}`}
          >
            {/* Header row placeholder */}
            <div className={`flex items-center justify-between mb-1 ${arClass(isRTL, "flex-row-reverse")}`}>
              <div className={`flex items-center gap-2.5 ${arClass(isRTL, "flex-row-reverse")}`}>
                <div
                  className={`w-8 h-8 rounded-full ${darkMode ? "bg-gray-600" : "bg-gray-200"} animate-pulse`}
                />
                <div
                  className={`h-4 w-24 rounded ${darkMode ? "bg-gray-600" : "bg-gray-200"} animate-pulse`}
                />
              </div>
              <div className={`flex items-center gap-2 ${arClass(isRTL, "flex-row-reverse")}`}>
                <div
                  className={`h-5 w-12 rounded ${darkMode ? "bg-gray-600" : "bg-gray-200"} animate-pulse`}
                />
                <div
                  className={`h-4 w-10 rounded ${darkMode ? "bg-gray-600" : "bg-gray-200"} animate-pulse`}
                />
                <div
                  className={`h-5 w-16 rounded-full ${darkMode ? "bg-gray-600" : "bg-gray-200"} animate-pulse`}
                />
              </div>
            </div>
            {/* Chart placeholder */}
            <div
              className={`h-[200px] mt-3 rounded ${darkMode ? "bg-gray-600" : "bg-gray-100"} animate-pulse`}
            />
            {/* Button placeholder */}
            <div
              className={`h-4 w-24 rounded mt-2.5 ${darkMode ? "bg-gray-600" : "bg-gray-200"} animate-pulse`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

function VitalsSection() {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const { requests } = useSelector((state) => state.api);
  const { id } = useParams();
  const patientKey = `getPatient_${id}`;
  const patientLoading = requests[patientKey]?.loading;
  const [chartData, setChartData] = useState(null);
  const [graphMode, setGraphMode] = useState("month");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Get current language
  const currentLanguage = useSelector((state) => state.settings.defaultLanguage);
  const isRTL = currentLanguage === "ar";

  useEffect(() => {
    const fetchVitals = async () => {
      try {
        const res = await api.get(
          `visits/vitals-history/${id}?graphMode=${graphMode}&year=${selectedYear}&month=${selectedMonth}`,
        );

        const apiData = res.data.data;

        // transform BP
        const bp = apiData.bpData.map((item) => ({
          x: item.x,
          systolic: item.systolic,
          diastolic: item.diastolic,
        }));

        // transform others
        const weight = apiData.weightData.map((i) => ({
          x: i.x,
          value: i.y,
        }));

        const height = apiData.heightData.map((i) => ({
          x: i.x,
          value: i.y,
        }));

        const temperature = apiData.temperatureData.map((i) => ({
          x: i.x,
          value: i.y,
        }));

        const heartRate = apiData.heartRateData.map((i) => ({
          x: i.x,
          value: i.y,
        }));

        setChartData({
          bp,
          weight,
          height,
          temperature,
          heartRate,
          stats: res.data.stats,
        });
      } catch (err) {
        console.error("Failed to load chart data", err);
      }
    };

    fetchVitals();
  }, [id, graphMode, selectedYear, selectedMonth]);

  // Show placeholder while loading
  if (patientLoading) {
    return <VitalsSectionPlaceholder darkMode={darkMode} />;
  }

  // Helper function to get BP status with translations
  const getBPStatus = () => {
    if (!chartData || !chartData.stats || !chartData.stats.bp) return textTranslator("normal", "VISITS");

    const bpStats = chartData.stats.bp;
    const avgSystolic = bpStats.systolic?.avg || 0;
    const avgDiastolic = bpStats.diastolic?.avg || 0;

    if (avgSystolic > 140 || avgDiastolic > 90) return textTranslator("high", "VISITS");
    if (avgSystolic < 90 || avgDiastolic < 60) return textTranslator("low", "VISITS");
    return textTranslator("normal", "VISITS");
  };

  // Helper function to get BP value display
  const getBPValue = () => {
    if (!chartData || !chartData.stats || !chartData.stats.bp) return "120/80";

    const bpStats = chartData.stats.bp;
    const systolic = bpStats.systolic?.avg || 0;
    const diastolic = bpStats.diastolic?.avg || 0;
    
    return `${systolic}/${diastolic}`;
  };

  // Helper function to get BP min value
  const getBPMin = () => {
    if (!chartData || !chartData.stats || !chartData.stats.bp) return 60;
    const bpStats = chartData.stats.bp;
    return Math.min(bpStats.systolic?.min || 0, bpStats.diastolic?.min || 0);
  };

  // Helper function to get BP max value
  const getBPMax = () => {
    if (!chartData || !chartData.stats || !chartData.stats.bp) return 160;
    const bpStats = chartData.stats.bp;
    return Math.max(bpStats.systolic?.max || 0, bpStats.diastolic?.max || 0);
  };

  // Helper function to get BP avg value (display as systolic/diastolic)
  const getBPAvg = () => {
    if (!chartData || !chartData.stats || !chartData.stats.bp) return "120/80";
    const bpStats = chartData.stats.bp;
    return `${bpStats.systolic?.avg || 0}/${bpStats.diastolic?.avg || 0}`;
  };

  // Helper function to get status based on value and thresholds with translations
  const getStatus = (
    value,
    highThreshold,
    lowThreshold,
    highLabelKey = "high",
    lowLabelKey = "low",
    normalThreshold = null,
    normalLabelKey = "normal",
  ) => {
    if (!chartData || value === undefined || value === null) return textTranslator(normalLabelKey, "VISITS");

    // If normalThreshold is provided, check if value is within normal range
    if (normalThreshold !== null && typeof normalThreshold === "object") {
      const { min, max } = normalThreshold;
      if (value >= min && value <= max) return textTranslator(normalLabelKey, "VISITS");
    }

    if (value > highThreshold) return textTranslator(highLabelKey, "VISITS");
    if (value < lowThreshold) return textTranslator(lowLabelKey, "VISITS");
    return textTranslator(normalLabelKey, "VISITS");
  };

  return (
    <div
      className={`rounded-2xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border p-5 md:p-6
                    ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-slate-100"}`}
    >
      {/* header */}
      <div className={`flex items-center justify-between mb-5 ${arClass(isRTL, "flex-row-reverse")}`}>
        <div className={`flex items-center gap-2.5 ${arClass(isRTL, "flex-row-reverse")}`}>
          <Activity
            className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-blue-500"}`}
          />
          <Text>Vitals Overview</Text>
        </div>
        <div className={`flex gap-2 ${arClass(isRTL, "flex-row-reverse")}`}>
          <CustomSelect
            options={[
              { value: "month", label: textTranslator("month", "VISITS") },
              { value: "year", label: textTranslator("year", "VISITS") },
            ]}
            value={graphMode}
            onChange={(e) => setGraphMode(e.target.value)}
            placeholder={textTranslator("selectGraphMode", "VISITS")}
            darkMode={darkMode}
            isRTL={isRTL}
            allowSearch={false}
            allowCreate={false}
            mainClassName={`min-w-[120px] `}
          />
          {graphMode === "month" && (
            <CustomSelect
              options={[...Array(12)].map((_, i) => ({
                value: i + 1,
                label: formtDate(new Date(0, i), false, true),
              }))}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              placeholder={textTranslator("selectMonth", "VISITS")}
              darkMode={darkMode}
              isRTL={isRTL}
              allowSearch={false}
              allowCreate={false}
              mainClassName={`min-w-[120px] `}
            />
            
          )}
          <CustomSelect
              options={[...Array(10)].map((_, i) => ({
                value: new Date().getFullYear() - i,
                label: new Date().getFullYear() - i,
              }))}
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              placeholder={textTranslator("selectYear", "VISITS")}
              darkMode={darkMode}
              isRTL={isRTL}
              allowSearch={false}
              allowCreate={false}
              mainClassName={`min-w-[120px] `}
            />

          
        </div>
      </div>

      {/* grid of 4 vitals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VitalCard
          title={textTranslator("bloodPressure","VISITS")}
          value={getBPValue()}
          unit={textTranslator("mmHg","VISITS")}
          status={getBPStatus()}
          statusColor={darkMode ? "#4ADE80" : "#15803D"}
          statusBg={darkMode ? "rgba(74,222,128,0.2)" : "#DCFCE7"}
          color={darkMode ? "#60A5FA" : "#3B82F6"}
          data={chartData ? chartData.bp : null}
          darkMode={darkMode}
          isRTL={isRTL}
          lines={[
            {
              key: "systolic",
              stroke: darkMode ? "#60A5FA" : "#3B82F6",
              name: "systolic",
            },
            {
              key: "diastolic",
              stroke: darkMode ? "#93C5FD" : "#93C5FD",
              dash: "5 3",
              name: "diastolic",
            },
          ]}
          minValue={getBPMin()}
          maxValue={getBPMax()}
          averageValue={getBPAvg()}
           graphMode={graphMode} // Add this line
        />
        <VitalCard
          title={textTranslator("temperature","VISITS")}
          value={
            chartData ? chartData.stats.temperature.avg.toFixed(1) : "98.6"
          }
          unit={textTranslator("°C", "VISITS")}
          status={getStatus(
            chartData?.stats?.temperature?.avg,
            38,
            36.5,
            "fever",
            "hypothermia",
            { min: 36.5, max: 37.5 },
            "normal",
          )}
          statusColor={darkMode ? "#F87171" : "#DC2626"}
          statusBg={darkMode ? "rgba(248,113,113,0.2)" : "#FEE2E2"}
          color={darkMode ? "#F87171" : "#EF4444"}
          data={chartData ? chartData.temperature : null}
          darkMode={darkMode}
          isRTL={isRTL}
          minValue={chartData ? chartData.stats.temperature.min : 95}
          maxValue={chartData ? chartData.stats.temperature.max : 105}
          averageValue={chartData ? chartData.stats.temperature.avg : 98.6}
           graphMode={graphMode} // Add this line
        />
        <VitalCard
          title={textTranslator("weight","VISITS")}
          value={chartData ? chartData.stats.weight.avg.toFixed(1) : "75"}
          unit={textTranslator("kg", "VISITS")}
          status={getStatus(
            chartData?.stats?.weight?.avg,
            90,
            60,
            "overweight",
            "underweight",
            { min: 60, max: 90 },
            "healthy",
          )}
          statusColor={darkMode ? "#FBBF24" : "#D97706"}
          statusBg={darkMode ? "rgba(251,191,36,0.2)" : "#FEF3C7"}
          color={darkMode ? "#FBBF24" : "#F59E0B"}
          data={chartData ? chartData.weight : null}
          darkMode={darkMode}
          isRTL={isRTL}
          minValue={chartData ? chartData.stats.weight.min : 50}
          maxValue={chartData ? chartData.stats.weight.max : 100}
          averageValue={chartData ? chartData.stats.weight.avg : 75}
           graphMode={graphMode} // Add this line
        />
        <VitalCard
          title={textTranslator("heartRate","VISITS")}
          value={chartData ? chartData.stats.heartRate.avg.toFixed(0) : "72"}
          unit={textTranslator("BPM", "VISITS")}
          status={getStatus(
            chartData?.stats?.heartRate?.avg,
            100,
            60,
            "tachycardia",
            "bradycardia",
            { min: 60, max: 100 },
            "normal",
          )}
          statusColor={darkMode ? "#4ADE80" : "#15803D"}
          statusBg={darkMode ? "rgba(74,222,128,0.2)" : "#DCFCE7"}
          color={darkMode ? "#4ADE80" : "#22C55E"}
          data={chartData ? chartData.heartRate : null}
          darkMode={darkMode}
          isRTL={isRTL}
          minValue={chartData ? chartData.stats.heartRate.min : 50}
          maxValue={chartData ? chartData.stats.heartRate.max : 120}
          averageValue={chartData ? chartData.stats.heartRate.avg : 80}
           graphMode={graphMode} // Add this line
        />
      </div>
    </div>
  );
}

export default VitalsSection;